/* eslint-disable camelcase */
import React, { useMemo } from 'react';
import iamCoreClient from './lib/iam-core-client';
import OktaSignInWidget from './OktaSignInWidget';
import sessionStorageUtil from './lib/session-storage-util';
import Loader from './Loader';
import { logPreAuthErrorEvent, logSpaLandingEvent } from './lib/amplitude-utils';
import { RedirectableError, ValidationError } from './lib/errors';
import {
  authorizeToOkta,
  enrichApiResponse,
  getPostAuthorizeParams,
  getProxiedAuthorizeValues,
  getUsernameFromSession,
  parsedPayload,
  postAuthorizePostFhirForm
} from './lib/authorize-utils';
import { useHash, useQuery } from './lib/router-utils';
import { initAuthorize } from './lib/scripts/page-specific/oauth2/authorize';
import { useFramingContext } from './FrameRestricter';
import { useOktaAuth } from '@okta/okta-react';
import { getXRoutingDeploymentValue, recordAuthorizeUrl, removeTAndCShownCookie } from './lib/cookie-utils';
import { addHTI1Scopes } from './lib/scope-mapping-utils';
import { logger } from "./lib/logger";


function validateRequest({ clientId, redirectUri, scopeString, state, responseType, fhir, aud, resource }) {
  if (!clientId) {
    throw new ValidationError(
      'Error: Missing client_id query parameter. You must provide a client_id.'
    );
  }
  if (!redirectUri) {
    throw new ValidationError(
      'Error: Missing redirect_uri in the request_url. A redirect_uri must be provided in the request_url.'
    );
  }
  try {
    new URL(redirectUri);
  } catch (err) {
    throw new ValidationError(
      'Error: Malformed redirect_uri. The redirect_uri param must be a valid URL.'
    );
  }
  if (!scopeString || !state || !responseType) {
    throw new RedirectableError(
      'Error: Missing one or more of required query parameters: scope, state, and response_type',
      redirectUri
    );
  }
  // For FHIR requests, require either aud or resource parameter (MCP flow uses resource per RFC8707)
  if (fhir && !aud && !resource) {
    throw new RedirectableError(
      'Error: Missing required aud or resource query parameter.',
      redirectUri
    );
  }
}

const getParamsToSet = ({ configs, createAccount, hash, payload, apiResponse }) => {
  const terminalErrorOccurred = configs.emailTokenLoggedInUserMismatchError || configs.userAppTypeMismatchError;
  const shouldRenderAosw = ['#signin/athena-forgot-password', '#create'].includes(hash)
      || terminalErrorOccurred || (createAccount && !configs.stateToken);
  if (!shouldRenderAosw) {
    return {
      authorizeToOktaParams: {
        proxiedAuthorizeValues: getProxiedAuthorizeValues({
          parsedPayload: payload,
          apiResponse,
          preAuthnRequestPayload: {
            oauthCodeChallenge: payload.oauthCodeChallenge,
            emailToken: payload.emailToken,
            fhir: payload.fhir,
            idp: configs.idp,
            xRoutingDeployment: getXRoutingDeploymentValue(),
          },
        }),
      },
    };
  } else {
    return { preAuthnConfigs: configs };
  }
};

async function generateRequest(
  {
    clientId,
    scopeString,
    redirectUri,
    aud,
    emailToken,
    idp,
    fhir,
    authServerId,
    uiLocales,
  },
  oktaAuth
) {
  const preAuthnRequest = {
    clientId,
    scope: scopeString,
    redirectUri,
    aud,
    emailToken,
    idp,
    fhir,
    authServerId,
    uiLocales,
    source: 'lwa',
  };
  if (emailToken) {
    const loggedInUsername = await getUsernameFromSession(oktaAuth);
    preAuthnRequest.loggedInUsername = loggedInUsername;
  }
  return preAuthnRequest;
}

function getErrorToSet(error, redirectUri) {
  if (error.message && error.redirectable) {
    return new RedirectableError(error.message, redirectUri);
  } else {
    return error;
  }
}

export default function PreAuthorize() {
  const [ preAuthnConfigs, setPreAuthnConfigs ] = React.useState();
  const [ authorizeToOktaParams, setAuthorizeToOktaParams ] = React.useState();
  const [ error, setIsError ] = React.useState();
  const [ authorizeToOktaUrl, setAuthorizeToOktaUrl ] = React.useState();
  const [ finalIdp, setFinalIdp ] = React.useState();
  const authorizePostParamsObject = getPostAuthorizeParams();
  let authorizePostParams;
  const isPostAuthorizeWorkflow = Object.keys(authorizePostParamsObject).length > 0;
  if (isPostAuthorizeWorkflow) {
    authorizePostParamsObject.scope = addHTI1Scopes(authorizePostParamsObject.scope);
    authorizePostParams = new URLSearchParams(authorizePostParamsObject);
  }
  const searchParams = useQuery(authorizePostParams);
  const hash = useHash();
  let payload = useMemo(() => parsedPayload(searchParams), [searchParams]);
  payload.authorizePostParams = JSON.stringify(authorizePostParamsObject);

  const { oktaAuth } = useOktaAuth();
  const { allowFraming, shouldRenderContent } = useFramingContext();
  const {
    isFhirEhrLaunch,
    clientId,
    scopeString,
    redirectUri,
    resource,
    aud,
    authorizeRedirectUrl,
    fhir,
    responseType,
    prompt,
    sessionToken,
    state,
    createAccount,
    errorPayloadFromOhsw,
  } = payload;

  function _fetchData(preAuthnInfo) {
    //swagger 2.0 doesn't like nulls
    preAuthnInfo = Object.fromEntries(
      Object.entries(preAuthnInfo).filter(([ _, v ]) => v != null)
    );
    return iamCoreClient.fetchPreAuthnConfig({ preAuthnInfo });
  }

  React.useEffect(() => {
    async function setPreAuthn() {
      try {
        removeTAndCShownCookie();
        if (errorPayloadFromOhsw && !isPostAuthorizeWorkflow) {
          if (!isPostAuthorizeWorkflow) {
            const url = new URL(window.location.href);
            url.searchParams.delete('error_details');
            window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
          }
          logger.error('Error occurred in OHSW', JSON.stringify(errorPayloadFromOhsw));
          setIsError(getErrorToSet({ ...errorPayloadFromOhsw, isOhswError: true }, redirectUri));
        }

        // Build the original authorize URL with derived aud for MCP flow
        const originalAuthUrl = new URL(window.location.href);
        if (aud && resource && !originalAuthUrl.searchParams.has('aud')) {
          originalAuthUrl.searchParams.set('aud', aud);
        }
        const originalAuthUrlString = originalAuthUrl.toString();
        recordAuthorizeUrl(originalAuthUrlString);
        sessionStorageUtil.updateOriginalAuthUrl(originalAuthUrlString);

        await logSpaLandingEvent({ client_id: clientId });
        if (errorPayloadFromOhsw) {
          return;
        }
        validateRequest({
          clientId,
          redirectUri,
          scopeString,
          state,
          responseType,
          aud,
          resource,
          fhir,
        });
        if (
          (prompt === 'none' && !sessionToken)
          || (isFhirEhrLaunch && (await oktaAuth.session.get()).id)
        ) {
          allowFraming();
          setAuthorizeToOktaUrl(authorizeRedirectUrl.toString());
        } else {
          const codeVerifier = oktaAuth.pkce.generateVerifier();
          const codeChallenge = await oktaAuth.pkce.computeChallenge(
            codeVerifier
          );
          payload = {
            ...payload,
            oauthCodeVerifier: codeVerifier,
            oauthCodeChallenge: codeChallenge,
          };

          const preAuthnRequest = await generateRequest(payload, oktaAuth);
          const apiResponse = await _fetchData(preAuthnRequest);

          const configs = enrichApiResponse({
            apiResponse,
            parsedPayload: payload,
          });
          const finalIdp = configs.idp;
          if (finalIdp) {
            setFinalIdp(finalIdp);
            // we're going to immediately redirect through to okta's idp authorization flow via the signin-widget
            // without any user interaction so we dont care about being iframe'd for federated idp flows
            allowFraming();
          }
          const paramsToSet = getParamsToSet({ configs, createAccount, hash, payload, apiResponse });
          if (paramsToSet.authorizeToOktaParams) {
            setAuthorizeToOktaParams(paramsToSet.authorizeToOktaParams);
          } else {
            setPreAuthnConfigs(paramsToSet.preAuthnConfigs);
          }
        }
      } catch (error) {
        await logPreAuthErrorEvent({ error_message: error.message });
        setIsError(getErrorToSet(error, redirectUri));
      }
    }
    setPreAuthn();
  }, []);

  React.useEffect(() => {
    if (error && shouldRenderContent) {
      throw error;
    }
  }, [ error, shouldRenderContent ]);

  if (shouldRenderContent) {
    if (authorizeToOktaUrl) {
      if (Object.keys(JSON.parse(payload.authorizePostParams)).length > 0) {
        const originalPostAuthorizationParams = JSON.parse(payload.authorizePostParams);
        if (finalIdp) {
          originalPostAuthorizationParams.idp = finalIdp;
        }
        return postAuthorizePostFhirForm({
          originalPostAuthorizationParams,
          authorizeToOktaUrl,
        });
      } else {
        window.location.assign(authorizeToOktaUrl);
      }
      return null;
    }
    if (authorizeToOktaParams) {
      authorizeToOkta({ ...authorizeToOktaParams, oktaAuth });
    }
    if (preAuthnConfigs) {
      return (
        <OktaSignInWidget
          {...preAuthnConfigs}
          renderWidgetFunction={initAuthorize}
        />
      );
    }
  }
  return <Loader />;
}
